import React, { useState, useRef, useEffect, useCallback  } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Modal,
  TextField,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import StyleIcon from "@mui/icons-material/Style";
import katex from "katex";
import "katex/dist/katex.min.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import sanitizeHtml from "sanitize-html";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import TagModal from "./TagModal";
import { getToken } from "../utils/Authe";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useParams } from "react-router-dom";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MCQ_ENDPOINT = "/subfolder";

const MultipleChoice = () => {
  // State declarations
      const theme = useTheme();
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [folderName, setFolderName] = useState("Loading...");
  const [cards, setCards] = useState([]);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([""]);
  const [optionExplanations, setOptionExplanations] = useState([""]);
  const [isCorrect, setIsCorrect] = useState([false]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editOptions, setEditOptions] = useState([""]);
  const [editOptionExplanations, setEditOptionExplanations] = useState([""]);
  const [editIsCorrect, setEditIsCorrect] = useState([false]);
  const [editSelectedImage, setEditSelectedImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCardId, setDeleteCardId] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagCardIndex, setTagCardIndex] = useState(null);
  const [tagModalPosition, setTagModalPosition] = useState({ top: 0, left: 0 });
  const [collapsedQuestion, setCollapsedQuestion] = useState(false);
  const [collapsedOptions, setCollapsedOptions] = useState(false);
  const [questionCharCount, setQuestionCharCount] = useState(0);
  const [optionCharCounts, setOptionCharCounts] = useState([0]);
  const [explanationCharCounts, setExplanationCharCounts] = useState([0]);
  const CHAR_LIMIT = 255;
const [showImage, setShowImage] = useState({});
const [optionKey, setOptionKey] = useState(0);

  const optionRefs = useRef([]);
  const token = getToken();
  const { id } = useParams();

  const permissions = {
    can_create_mcq: true,
    can_read_mcq: true,
    can_update_mcq: true,
    can_delete_mcq: true,
    can_tag_mcq: true,
    can_start_flashcard: true,
    can_start_quiz: true,
    is_admin: true,
  };

  // API Functions
  const fetchMCQQuestions = async (subfolderId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MCQ_ENDPOINT}/${subfolderId}/mcq/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch MCQ questions: ${response.status}`);
      }
      const data = await response.json();
      return data.map((item) => ({
        id: item.id,
        question: item.statement,
        options: item.answers.map((a) => a.answer_text),
        optionExplanations: item.answers.map((a) => a.explanation),
        isCorrect: item.answers.map((a) => a.is_correct),
        image: item.image ? `${API_BASE_URL}${item.image}` : null,
        tags: item.tags || [],
        expanded: false,
      }));
    } catch (error) {
      console.error("Error fetching MCQ questions:", error);
      showSnackbar(`Error fetching MCQ questions: ${error.message}`, "error");
      return [];
    }
  };

  const createMCQQuestion = async (subfolderId, questionData) => {
    try {
      const formData = new FormData();
      formData.append("statement", questionData.statement);
      formData.append("question_type", "MCQ");
      const answers = questionData.options.map((option, index) => ({
        answer_text: option,
        is_correct: questionData.isCorrect[index],
        explanation: questionData.optionExplanations[index] || "", // allow empty
      }));
      formData.append("answers", JSON.stringify(answers));
      if (questionData.tags && questionData.tags.length > 0) {
        formData.append("tags", JSON.stringify(questionData.tags));
      }
      if (questionData.image) {
        formData.append("image", questionData.image);
      }
      const response = await fetch(
        `${API_BASE_URL}${MCQ_ENDPOINT}/${subfolderId}/mcq/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to create MCQ question: ${response.status}`);
      }
      const data = await response.json();
      showSnackbar("MCQ question created successfully!", "success");
      return {
        id: data.id,
        question: data.statement,
        options: data.answers.map((a) => a.answer_text),
        optionExplanations: data.answers.map((a) => a.explanation),
        isCorrect: data.answers.map((a) => a.is_correct),
        image: data.image || null,
        tags: data.tags || [],
        expanded: false,
      };
    } catch (error) {
      console.error("Error creating MCQ question:", error);
      showSnackbar(`Error creating MCQ question: ${error.message}`, "error");
      return null;
    }
  };

  const updateMCQQuestion = async (subfolderId, questionId, questionData) => {
    try {
      const formData = new FormData();
      formData.append("statement", questionData.statement);
      formData.append("question_type", "MCQ");
      const answers = questionData.options.map((option, index) => ({
        answer_text: option,
        is_correct: questionData.isCorrect[index],
        explanation: questionData.optionExplanations[index] || "",
      }));
      formData.append("answers", JSON.stringify(answers));
      if (questionData.tags && questionData.tags.length > 0) {
        formData.append("tags", JSON.stringify(questionData.tags));
      }
      if (questionData.image) {
        formData.append("image", questionData.image);
      }
      const response = await fetch(
        `${API_BASE_URL}${MCQ_ENDPOINT}/${subfolderId}/mcq/${questionId}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to update MCQ question: ${response.status}`);
      }
      const data = await response.json();
      showSnackbar("MCQ question updated successfully!", "success");
      return {
        id: data.id,
        question: data.statement,
        options: data.answers.map((a) => a.answer_text),
        optionExplanations: data.answers.map((a) => a.explanation),
        isCorrect: data.answers.map((a) => a.is_correct),
        image: data.image || null,
        tags: data.tags || [],
        expanded: false,
      };
    } catch (error) {
      console.error("Error updating MCQ question:", error);
      showSnackbar(`Error updating MCQ question: ${error.message}`, "error");
      return null;
    }
  };

  const deleteMCQQuestion = async (subfolderId, questionId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MCQ_ENDPOINT}/${subfolderId}/mcq/${questionId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to delete MCQ question: ${response.status}`);
      }
      showSnackbar("MCQ question deleted successfully!", "success");
      return true;
    } catch (error) {
      console.error("Error deleting MCQ question:", error);
      showSnackbar(`Error deleting MCQ question: ${error.message}`, "error");
      return false;
    }
  };

  const fetchFolderName = async (folderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/${folderId}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch folder name: ${response.status}`);
      }
      const data = await response.json();
      return data.name;
    } catch (error) {
      console.error("Error fetching folder name:", error);
      return "Unknown Folder";
    }
  };

  // Load questions on component mount
  useEffect(() => {
    const loadData = async () => {
      const name = await fetchFolderName(id);
      setFolderName(name);
      const questions = await fetchMCQQuestions(id);
      if (questions) {
        setCards(questions);
      }
      setIsLoading(false);
    };
    loadData();
  }, [id]);

  // Handlers
  const toggleQuestion = () => setCollapsedQuestion(!collapsedQuestion);
  const toggleOptions = () => setCollapsedOptions(!collapsedOptions);

const toggleImageVisibility = (cardId) => {
  setShowImage((prev) => ({
    ...prev,
    [cardId]: !prev[cardId],
  }));
};

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
    const text = value;
    const newCounts = [...optionCharCounts];
    newCounts[index] = text.length;
    setOptionCharCounts(newCounts);
  };

  const handleExplanationChange = (index, value) => {
    const newExplanations = [...optionExplanations];
    newExplanations[index] = value;
    setOptionExplanations(newExplanations);
    const text = value;
    const newCounts = [...explanationCharCounts];
    newCounts[index] = text.length;
    setExplanationCharCounts(newCounts);
  };

  const handleQuestionChange = (value) => {
    setQuestion(value);
    const text = value;
    setQuestionCharCount(text.length);
  };

  const handleCorrectChange = (index, checked) => {
    const newIsCorrect = [...isCorrect];
    newIsCorrect[index] = checked;
    setIsCorrect(newIsCorrect);
  };

  const handleAddOption = () => {
    setOptions([...options, ""]);
    setOptionExplanations([...optionExplanations, ""]);
    setIsCorrect([...isCorrect, false]);
    setOptionCharCounts([...optionCharCounts, 0]);
    setExplanationCharCounts([...explanationCharCounts, 0]);
  };

  const handleRemoveOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
    const newExplanations = [...optionExplanations];
    newExplanations.splice(index, 1);
    setOptionExplanations(newExplanations);
    const newIsCorrect = [...isCorrect];
    newIsCorrect.splice(index, 1);
    setIsCorrect(newIsCorrect);
    const newOptionCounts = [...optionCharCounts];
    newOptionCounts.splice(index, 1);
    setOptionCharCounts(newOptionCounts);
    const newExplanationCounts = [...explanationCharCounts];
    newExplanationCounts.splice(index, 1);
    setExplanationCharCounts(newExplanationCounts);
  };

  const handleOptionKeyPress = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setOptions((prev) => [...prev, ""]);
      setOptionExplanations((prev) => [...prev, ""]);
      setIsCorrect((prev) => [...prev, false]);
      setOptionCharCounts((prev) => [...prev, 0]);
      setExplanationCharCounts((prev) => [...prev, 0]);
      setTimeout(() => {
        const nextIndex = index + 1;
        if (optionRefs.current[nextIndex]) {
          optionRefs.current[nextIndex].focus();
        }
      }, 0);
    }
  };

  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleSaveQuestion = async () => {
  if (!permissions.can_create_mcq && !permissions.is_admin) {
    showSnackbar("You don't have permission to create an MCQ Questions.", "warning");
    return;
  }
  if (
    !question.trim() ||
    options.some((opt) => !opt.trim()) ||
    !isCorrect.some((correct) => correct)
  ) {
    showSnackbar(
      "Please complete question, all options, and select at least one correct option.",
      "warning"
    );
    return;
  }
  
  const questionData = {
    statement: question,
    options: options,
    optionExplanations: optionExplanations,
    isCorrect: isCorrect,
    image: selectedImage,
    tags: [],
  };
  
  const newQuestion = await createMCQQuestion(id, questionData);
  if (newQuestion) {
    setCards([...cards, newQuestion]);
    setQuestion("");
    setOptions([""]);
    setOptionExplanations([""]);
    setIsCorrect([false]);
    setSelectedImage(null);
    setQuestionCharCount(0);
    setOptionCharCounts([0]);
    setExplanationCharCounts([0]);
    setOptionKey(prev => prev + 1); // Add this line
    showSnackbar("Questions added successfully!", "success");
  }
};

  const handleConfirmDuplicate = async () => {
    if (!permissions.can_create_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to create an MCQ Questions.", "warning");
      return;
    }
    const questionData = {
    statement: question, // Send as-is
    options: options,
    optionExplanations: optionExplanations,
    isCorrect: isCorrect,
    image: selectedImage,
    tags: [],
  };
  const newQuestion = await createMCQQuestion(id, questionData);
    if (newQuestion) {
      setCards([...cards, newQuestion]);
      setQuestion("");
      setOptions([""]);
      setOptionExplanations([""]);
      setIsCorrect([false]);
      setSelectedImage(null);
      setQuestionCharCount(0);
      setOptionCharCounts([0]);
      setExplanationCharCounts([0]);
      setDuplicateDialogOpen(false);
      showSnackbar("Questions added successfully!", "success");
    }
  };

  const handleCancelDuplicate = () => setDuplicateDialogOpen(false);

  const toggleCardExpand = (index) => {
    const newCards = [...cards];
    newCards[index].expanded = !newCards[index].expanded;
    setCards(newCards);
  };

  const openEditModal = (card) => {
  if (!permissions.can_update_mcq && !permissions.is_admin) {
    showSnackbar("You don't have permission to update an MCQ Questions.", "warning");
    return;
  }
  setEditCard(card);
  setEditQuestion(card.question);
  setEditOptions([...card.options]);
  setEditOptionExplanations([...card.optionExplanations]);
  setEditIsCorrect([...card.isCorrect]);
  setEditSelectedImage(card.image);
  setEditModalOpen(true);
};


const handleEditOptionChange = useCallback((index, value) => {
  setEditOptions((prevOptions) => {
    const newOptions = [...prevOptions];
    newOptions[index] = value;
    return newOptions;
  });
}, []);

  const handleEditExplanationChange = useCallback((index, value) => {
  setEditOptionExplanations((prevExplanations) => {
    const newExplanations = [...prevExplanations];
    newExplanations[index] = value;
    return newExplanations;
  });
}, []);

  const handleEditCorrectChange = (index, checked) => {
    const newIsCorrect = [...editIsCorrect];
    newIsCorrect[index] = checked;
    setEditIsCorrect(newIsCorrect);
  };

  const addEditOption = () => {
    setEditOptions([...editOptions, ""]);
    setEditOptionExplanations([...editOptionExplanations, ""]);
    setEditIsCorrect([...editIsCorrect, false]);
  };

  const handleEditRemoveOption = (index) => {
    const newOptions = [...editOptions];
    newOptions.splice(index, 1);
    setEditOptions(newOptions);
    const newExplanations = [...editOptionExplanations];
    newExplanations.splice(index, 1);
    setEditOptionExplanations(newExplanations);
    const newIsCorrect = [...editIsCorrect];
    newIsCorrect.splice(index, 1);
    setEditIsCorrect(newIsCorrect);
  };

  const handleUpdateCard = async () => {
    if (!permissions.can_update_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to update an MCQ Questions.", "warning");
      return;
    }
    if (
      !editQuestion.trim() ||
      editOptions.some((opt) => !opt.trim()) ||
      !editIsCorrect.some((correct) => correct)
    ) {
      showSnackbar(
        "Please complete question, all options, and select at least one correct option.",
        "warning"
      );
      return;
    }
     const questionData = {
    statement: editQuestion, // Send as-is
    options: editOptions,
    optionExplanations: editOptionExplanations,
    isCorrect: editIsCorrect,
    image: editSelectedImage,
    tags: editCard.tags || [],
  };
  const updatedQuestion = await updateMCQQuestion(id, editCard.id, questionData);
    if (updatedQuestion) {
      const updatedCards = cards.map((card) =>
        card.id === editCard.id ? updatedQuestion : card
      );
      setCards(updatedCards);
      setEditModalOpen(false);
      showSnackbar("Question updated successfully!", "success");
    }
  };

  const handleCloseModal = () => setEditModalOpen(false);

  const openDeleteDialog = (id) => {
    if (!permissions.can_delete_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to delete an MCQ Questions.", "warning");
      return;
    }
    setDeleteCardId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCard = async () => {
    const subfolderId = id;
    const success = await deleteMCQQuestion(subfolderId, deleteCardId);
    if (success) {
      const updatedCards = cards.filter((card) => card.id !== deleteCardId);
      setCards(updatedCards);
      setDeleteDialogOpen(false);
      showSnackbar("Question deleted successfully!", "success");
    }
  };

  const handleCloseDeleteDialog = () => setDeleteDialogOpen(false);

  const openTagModal = (index, event) => {
    if (!permissions.can_tag_mcq && !permissions.is_admin) {
      showSnackbar("You don't have permission to tag an MCQ Questions.", "warning");
      return;
    }
    const iconRect = event.target.getBoundingClientRect();
    setTagCardIndex(index);
    setTagModalPosition({
      top: iconRect.bottom + window.scrollY,
      left: iconRect.left + window.scrollX,
    });
    setTagModalOpen(true);
  };

  const handleTagAdded = (cardId, newTag) => {
    if (!cardId || !newTag || !newTag.name) {
      showSnackbar("Invalid tag data. Please try again.", "error");
      return;
    }
    const updatedCards = cards.map((card) =>
      card.id === cardId
        ? {
            ...card,
            tags: [...card.tags, { name: newTag.name, id: newTag.id || Date.now() }],
          }
        : card
    );
    setCards(updatedCards);
    if (editCard && editCard.id === cardId) {
      setEditCard({
        ...editCard,
        tags: [
          ...editCard.tags,
          { name: newTag.name, id: newTag.id || Date.now() },
        ],
      });
    }
    setTagModalOpen(false);
    showSnackbar("Tag added successfully!", "success");
  };

  const handleTagUpdated = (cardId, updatedTag) => {
    if (!cardId || !updatedTag || !updatedTag.id || !updatedTag.name) {
      showSnackbar("Invalid tag update data. Please try again.", "error");
      return;
    }
    const updatedCards = cards.map((card) =>
      card.id === cardId
        ? {
            ...card,
            tags: card.tags.map((tag) =>
              tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
            ),
          }
        : card
    );
    setCards(updatedCards);
    if (editCard && editCard.id === cardId) {
      setEditCard({
        ...editCard,
        tags: editCard.tags.map((tag) =>
          tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
        ),
      });
    }
    showSnackbar("Tag updated successfully!", "success");
  };

  const handleTagDeleted = (cardId, tagId) => {
    if (!cardId || !tagId) {
      showSnackbar("Invalid tag deletion data. Please try again.", "error");
      return;
    }
    const updatedCards = cards.map((card) =>
      card.id === cardId
        ? { ...card, tags: card.tags.filter((tag) => tag.id !== tagId) }
        : card
    );
    setCards(updatedCards);
    if (editCard && editCard.id === cardId) {
      setEditCard({
        ...editCard,
        tags: editCard.tags.filter((tag) => tag.id !== tagId),
      });
    }
    showSnackbar("Tag deleted successfully!", "success");
  };

  const renderContent = (html) => {
    if (!html) return null;
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);
    return (
      <div>
        {parts.map((part, index) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2);
            try {
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: katex.renderToString(latex, { displayMode: true }),
                  }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              return (
                <div key={index} className="error-content">
                  {part}
                </div>
              );
            }
          }
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1);
            try {
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: katex.renderToString(latex, { displayMode: false }),
                  }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              return (
                <span key={index} className="error-content">
                  {part}
                </span>
              );
            }
          }
          try {
            const formattedContent = part.replace(/\n/g, "<br />");
            const allowedTags = [
              "b",
              "i",
              "em",
              "strong",
              "span",
              "br",
              "u",
              "sub",
              "sup",
              "a",
              "p",
              "img",
            ];
            const allowedAttributes = {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
              p: ["style"],
            };
            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags,
              allowedAttributes,
              transformTags: { p: "span", div: "span" },
            });
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            return (
              <span key={index} className="error-content">
                {part}
              </span>
            );
          }
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: theme.palette.background.default,}}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.palette.background.default,
        padding: "20px 20px",
      }}
    >
        <style>
  {`
    ${theme.palette.mode === 'dark' ? `
      /* Default toolbar icon colors */
      .ql-toolbar .ql-stroke {
        stroke: #e5e7eb !important;
      }
      .ql-toolbar .ql-fill {
        fill: #e5e7eb !important;
      }
      .ql-toolbar .ql-picker-label {
        color: #e5e7eb !important;
      }

      /* Hover and focus states */
      .ql-toolbar button:hover .ql-stroke,
      .ql-toolbar button:focus .ql-stroke {
        stroke: #667eea !important;
      }
      .ql-toolbar button:hover .ql-fill,
      .ql-toolbar button:focus .ql-fill {
        fill: #667eea !important;
      }
      .ql-toolbar button:hover,
      .ql-toolbar button:focus {
        background-color: rgba(102, 126, 234, 0.2) !important;
      }

      /* Active state (when button is clicked/selected) */
      .ql-toolbar button.ql-active .ql-stroke {
        stroke: #667eea !important;
      }
      .ql-toolbar button.ql-active .ql-fill {
        fill: #667eea !important;
      }
      .ql-toolbar button.ql-active {
        background-color: rgba(102, 126, 234, 0.3) !important;
      }

      /* Editor placeholder and text color */
      .ql-editor.ql-blank::before {
        color: #9ca3af !important;
      }
      .ql-editor {
        color: #e5e7eb !important;
      }

      /* Toolbar and container border */
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: rgba(255, 255, 255, 0.12) !important;
      }
    ` : ''}
  `}
</style>
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          background: theme.palette.background.paper,
          borderRadius: "24px",
          padding: "40px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header Section */}
       <div
  style={{
    marginBottom: "40px",
    paddingBottom: "30px",
    borderBottom: "2px solid #e5e7eb",
  }}
>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "20px",
    }}
  >
    <div>
      <h1
        style={{
          margin: "0 0 10px 0",
          fontSize: "2.5rem",
          fontWeight: "800",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        Multiple Choice Questions
      </h1>
      <div
        style={{
          fontSize: "1.25rem",
          color: theme.palette.text.secondary,
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >

        <i className="bi bi-folder2-open" style={{ color: "#667eea" }}></i>
        {folderName}
      </div>
    </div>
     <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
  <Tooltip title="KaTeX Documentation" arrow>
    <IconButton
      onClick={() => window.open("https://katex.org/docs/supported.html", "_blank")}
      sx={{
        bgcolor: "rgba(102, 126, 234, 0.1)",
        "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
        p: 1.5,
        borderRadius: "50%",
      }}
    >
      <HelpOutlineIcon sx={{ color: "#667eea" }} />
    </IconButton>
  </Tooltip>

    
    <Tooltip title="User Guide" arrow>
    <IconButton
      onClick={() => setInfoModalOpen(true)}
      sx={{
        bgcolor: "rgba(102, 126, 234, 0.1)",
        "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
        p: 1.5,
        borderRadius: "50%",
      }}
    >
      <InfoOutlinedIcon sx={{ color: "#667eea" }} />
    </IconButton>
    </Tooltip>
  </div>
</div>
</div>


        {/* Input Form Section */}
        <div
          style={{
           background: theme.palette.mode === 'light'
  ? "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)"
  : "transparent",
            borderRadius: "16px",
            padding: "30px",
            marginBottom: "40px",
            border: "2px solid #e0e7ff",
          }}
        >
          {/* Question Field */}
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginBottom: "12px",
                padding: "12px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: "10px",
                transition: "all 0.3s ease",
              }}
              onClick={toggleQuestion}
            >
              <div style={{ marginRight: "12px", fontSize: "1.2rem" }}>
                {collapsedQuestion ? <FaChevronDown /> : <FaChevronUp />}
              </div>
              <div style={{ fontWeight: "600", fontSize: "1.1rem" }}>
                Question <span style={{ color: "#ef4444" }}>*</span>
              </div>
            </div>
            <div
              style={{
                display: collapsedQuestion ? "none" : "block",
                position: "relative",
              }}
            >
              <div
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid #e0e7ff",
                  background: theme.palette.background.default,
                    color: theme.palette.text.primary, 
                }}
              >
                <ReactQuill
                  value={question}
                  onChange={handleQuestionChange}
                  placeholder="Enter your question"
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike"],
                      ["blockquote", "code-block"],
                      [{ header: 1 }, { header: 2 }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ script: "sub" }, { script: "super" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      [{ direction: "rtl" }],
                      [{ size: ["small", false, "large", "huge"] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ color: [] }, { background: [] }],
                      [{ font: [] }],
                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "font",
                    "size",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "blockquote",
                    "code-block",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                    "image",
                    "video",
                    "color",
                    "background",
                    "align",
                    "script",
                    { name: "preserve-spaces", type: "inline" },
                  ]}
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: "15px",
                  right: "15px",
                  fontSize: "13px",
                  fontWeight: "600",
                  background: "rgba(102, 126, 234, 0.1)",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  color:
                    questionCharCount > CHAR_LIMIT * 0.9
                      ? "#ef4444"
                      : "#667eea",
                }}
              >
                {questionCharCount}/{CHAR_LIMIT}
              </div>
            </div>
          </div>

          {/* Options Field */}
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginBottom: "12px",
                padding: "12px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: "10px",
                transition: "all 0.3s ease",
              }}
              onClick={toggleOptions}
            >
              <div style={{ marginRight: "12px", fontSize: "1.2rem" }}>
                {collapsedOptions ? <FaChevronDown /> : <FaChevronUp />}
              </div>
              <div style={{ fontWeight: "600", fontSize: "1.1rem" }}>
                Options <span style={{ color: "#ef4444" }}>*</span>
              </div>
            </div>
            <div style={{ display: collapsedOptions ? "none" : "block" }}>
              {options.map((option, index) => (
                <div
                  key={`${optionKey}-${index}`}
                  style={{
                    position: "relative",
                    marginBottom: "15px",
                    wordBreak: "break-word",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "2px solid #e0e7ff",
                    background: theme.palette.background.default,
                    padding: "15px",
                    paddingBottom: "50px",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "10px",
                       background: theme.palette.background.paper,
                    color: theme.palette.text.primary, 
                    }}
                  >
                   <ReactQuill
  value={option}
  onChange={(value) => handleOptionChange(index, value)}
  placeholder="Enter your option here"
  modules={{
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      ["clean"],
    ],
  }}
  style={{
    marginBottom: "10px",
    borderRadius: "8px",
    // background: "#f9fafb",
    border: "1px solid #e0e7ff",
  }}
/>
                  </div >
                  <div
                    style={{
                       background: theme.palette.background.paper,
                    color: theme.palette.text.primary, 
                    }}
                  >
                 <ReactQuill
  value={optionExplanations[index]}
  onChange={(value) => handleExplanationChange(index, value)}
  placeholder="Explanation for this option (optional)"
  modules={{
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      ["clean"],
    ],
  }}
  style={{
    marginBottom: "10px",
    borderRadius: "8px",
    // background: "#f9fafb",
    border: "1px solid #e0e7ff",
  }}
/> </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "15px",
                      left: "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: "13px",
                        fontWeight: "600",
                        background: "rgba(102, 126, 234, 0.1)",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        color: "#667eea",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isCorrect[index]}
                        onChange={(e) =>
                          handleCorrectChange(index, e.target.checked)
                        }
                        style={{ marginRight: "8px", cursor: "pointer" }}
                      />
                      Correct
                    </label>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleRemoveOption(index)}
                      size="small"
                      style={{
                        color: "#ef4444",
                        background: "rgba(239, 68, 68, 0.1)",
                        padding: "6px",
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "15px",
                      right: "15px",
                      fontSize: "13px",
                      fontWeight: "600",
                      background: "rgba(102, 126, 234, 0.1)",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      color:
                        explanationCharCounts[index] > CHAR_LIMIT * 0.9
                          ? "#ef4444"
                          : "#667eea",
                    }}
                  >
                    Exp: {explanationCharCounts[index]}/{CHAR_LIMIT}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "15px",
                      right: "160px",
                      fontSize: "13px",
                      fontWeight: "600",
                      background: "rgba(102, 126, 234, 0.1)",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      color:
                        optionCharCounts[index] > CHAR_LIMIT * 0.9
                          ? "#ef4444"
                          : "#667eea",
                    }}
                  >
                    Opt: {optionCharCounts[index]}/{CHAR_LIMIT}
                  </div>
                </div>
              ))}
              <Button
                variant="outlined"
                onClick={handleAddOption}
                sx={{
                  marginTop: "10px",
                  color: "#667eea",
                  borderColor: "#667eea",
                  "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.1)",
                    borderColor: "#667eea",
                  },
                }}
              >
                Add Option
              </Button>
            </div>
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "8px",
                color: theme.palette.text.secondary,
              }}
            >
              Upload Image (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setSelectedImage(e.target.files[0])}
              style={{
                display: "block",
                border: "1px solid #ccc",
                padding: "12px",
                borderRadius: "8px",
                width: "100%",
                background: theme.palette.background.default,
              }}
            />
            {selectedImage && (
              <p
                style={{
                  marginTop: "5px",
                  fontSize: "14px",
                  color: "#10b981",
                  fontWeight: "600",
                }}
              >
                Selected: {selectedImage.name}
              </p>
            )}
          </div>

          {/* Save Button */}
          <div style={{ textAlign: "center", marginTop: "30px" }}>
            <Button
              onClick={handleSaveQuestion}
              variant="contained"
              disabled={!(permissions.can_create_mcq || permissions.is_admin)}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "14px 40px",
                fontSize: "1.1rem",
                fontWeight: "700",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 35px rgba(102, 126, 234, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "#d1d5db",
                  color: "white",
                  opacity: 0.6,
                },
              }}
            >
              <i
                className="bi bi-plus-circle"
                style={{ marginRight: "10px", fontSize: "1.2rem" }}
              ></i>
              Add Question
            </Button>
          </div>
        </div>

        {/* Flashcards List */}
        {permissions.can_read_mcq || permissions.is_admin ? (
          <div>
            {cards.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                   background: theme.palette.mode === 'light'
  ? "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)"
  : theme.palette.background.default,
                  borderRadius: "16px",
                  border: "2px dashed #c7d2fe",
                }}
              >
                <i
                  className="bi bi-inbox"
                  style={{
                    fontSize: "4rem",
                    color: "#c7d2fe",
                    marginBottom: "20px",
                    display: "block",
                  }}
                ></i>
                <p
                  style={{
                    fontSize: "1.3rem",
                    color: "#6b7280",
                    fontWeight: "600",
                    margin: 0,
                  }}
                >
                  No Questions yet. Create your first one above!
                </p>
              </div>
            ) : (
              <ul style={{ padding: 0, listStyle: "none", margin: 0 }}>
                {cards.map((card, index) => (
                  <li
                    key={card.id}
                    style={{
                      position: "relative",
                      padding: "28px",
                      borderRadius: "16px",
                      marginBottom: "20px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      border: "2px solid #e5e7eb",
                      background:
                        theme.palette.background.paper,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 8px 30px rgba(102, 126, 234, 0.2)";
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.borderColor = "#c7d2fe";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 4px 20px rgba(0, 0, 0, 0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                    }}
                  >
                    {/* Question Section */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        paddingRight: "60px",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          padding: "8px 14px",
                          borderRadius: "10px",
                          fontWeight: "800",
                          fontSize: "1.1rem",
                          marginRight: "16px",
                          minWidth: "45px",
                          textAlign: "center",
                          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                        }}
                      >
                        Q
                      </div>
                      <span
                        style={{
                          flex: 1,
                          color: theme.palette.text.primary,
                          lineHeight: "1.7",
                          fontSize: "1.05rem",
                          fontWeight: "600",
                        }}
                      >
                        {renderContent(card.question)}
                      </span>
                    </div>

                    {/* Action Icons */}
                    <div
                      style={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        display: "flex",
                        gap: "14px",
                        zIndex: 10,
                        background: theme.palette.background.default,
                        padding: "10px 14px",
                        borderRadius: "12px",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <i
                        className={`bi ${
                          card.expanded
                            ? "bi-arrows-angle-contract"
                            : "bi-arrows-angle-expand"
                        }`}
                        title={card.expanded ? "Contract" : "Expand"}
                        onClick={() => toggleCardExpand(index)}
                        style={{
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          color: "#667eea",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#764ba2";
                          e.currentTarget.style.transform = "scale(1.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#667eea";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                      <i
                        className="bi bi-pencil-square"
                        title="Edit"
                        onClick={() => openEditModal(card)}
                        style={{
                          cursor:
                            permissions.can_update_mcq || permissions.is_admin
                              ? "pointer"
                              : "not-allowed",
                          opacity:
                            permissions.can_update_mcq || permissions.is_admin
                              ? 1
                              : 0.4,
                          fontSize: "1.2rem",
                          color: "#10b981",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (
                            permissions.can_update_mcq ||
                            permissions.is_admin
                          ) {
                            e.currentTarget.style.color = "#059669";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#10b981";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                      <i
                        className="bi bi-trash3"
                        title="Delete"
                        onClick={() => openDeleteDialog(card.id)}
                        style={{
                          cursor:
                            permissions.can_delete_mcq || permissions.is_admin
                              ? "pointer"
                              : "not-allowed",
                          opacity:
                            permissions.can_delete_mcq || permissions.is_admin
                              ? 1
                              : 0.4,
                          fontSize: "1.2rem",
                          color: "#ef4444",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (
                            permissions.can_delete_mcq ||
                            permissions.is_admin
                          ) {
                            e.currentTarget.style.color = "#dc2626";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#ef4444";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                      {/* <i
                        className="bi bi-tags"
                        title="Tag"
                        onClick={(e) => openTagModal(index, e)}
                        style={{
                          cursor:
                            permissions.can_tag_mcq || permissions.is_admin
                              ? "pointer"
                              : "not-allowed",
                          opacity:
                            permissions.can_tag_mcq || permissions.is_admin
                              ? 1
                              : 0.4,
                          fontSize: "1.2rem",
                          color: "#8b5cf6",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (permissions.can_tag_mcq || permissions.is_admin) {
                            e.currentTarget.style.color = "#7c3aed";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#8b5cf6";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i> */}
                    </div>

                    {/* Expanded Content */}
                    {card.expanded &&
                      (permissions.can_read_mcq || permissions.is_admin) && (
                        <div
                          style={{
                            marginTop: "30px",
                            paddingTop: "24px",
                            borderTop: "2px solid #e5e7eb",
                          }}
                        >
                          {/* Image */}
                          {card.image && (
                            <div
                              style={{ margin: "10px 0", textAlign: "center" }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => toggleImageVisibility(card.id)}
                                style={{
                                  marginBottom: "10px",
                                  color: "#667eea",
                                  borderColor: "#667eea",
                                }}
                              >
                                {showImage[card.id]
                                  ? "Hide Image"
                                  : "Show Image"}
                              </Button>
                              <br></br>
                              {showImage[card.id] && (
                                <img
                                  src={
                                    typeof card.image === "string"
                                      ? card.image
                                      : URL.createObjectURL(card.image)
                                  }
                                  alt="Flashcard Image"
                                  style={{
                                    maxWidth: "300px",
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                    border: "2px solid #e5e7eb",
                                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                                  }}
                                  onClick={() =>
                                    window.open(
                                      typeof card.image === "string"
                                        ? card.image
                                        : URL.createObjectURL(card.image),
                                      "_blank"
                                    )
                                  }
                                />
                              )}
                            </div>
                          )}

                          {/* Options */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "16px",
                              marginBottom: "20px",
                            }}
                          >
                            {card.options.map((option, i) => (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  padding: "16px",
                                  borderRadius: "12px",
                                  background: card.isCorrect[i]
                                    ? "linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)"
                                    : "linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)",
                                  borderLeft: `4px solid ${
                                    card.isCorrect[i] ? "#10b981" : "#ef4444"
                                  }`,
                                }}
                              >
                                <div
                                  style={{
                                    background: card.isCorrect[i]
                                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                      : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                    color: "white",
                                    padding: "8px 14px",
                                    borderRadius: "10px",
                                    fontWeight: "800",
                                    fontSize: "1.1rem",
                                    marginRight: "16px",
                                    minWidth: "45px",
                                    textAlign: "center",
                                    boxShadow: card.isCorrect[i]
                                      ? "0 4px 15px rgba(16, 185, 129, 0.3)"
                                      : "0 4px 15px rgba(239, 68, 68, 0.3)",
                                  }}
                                >
                                  {card.isCorrect[i] ? "✓" : "X"}
                                </div>
                                <span
                                  style={{
                                    flex: 1,
                                    color: "#1f2937",
                                    lineHeight: "1.7",
                                    fontSize: "1.02rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  {renderContent(option)}
                                </span>
                                {card.optionExplanations[i] && (
                                  <div
                                    style={{
                                      marginLeft: "30px",
                                      marginTop: "10px",
                                      padding: "12px",
                                      background: "rgba(16, 185, 129, 0.1)",
                                      borderRadius: "8px",
                                      borderLeft: "3px solid #10b981",
                                      color:'black'
                                    }}
                                  >
                                    <strong>Explanation:</strong>
                                    <span style={{ marginLeft: "5px", color:"black" }}>
                                      {renderContent(
                                        card.optionExplanations[i]
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Tags */}
                          {/* <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginTop: "20px",
                              flexWrap: "wrap",
                              padding: "16px",
                              background:
                                "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                              borderRadius: "12px",
                              border: "2px solid #e9d5ff",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#7c3aed",
                                fontWeight: "700",
                                fontSize: "1rem",
                              }}
                            >
                              <i className="bi bi-tags-fill"></i>
                              Tags:
                            </div>
                            {card.tags.length === 0 ? (
                              <span
                                style={{
                                  color: "#9ca3af",
                                  fontStyle: "italic",
                                  fontSize: "0.95rem",
                                }}
                              >
                                No tags added yet
                              </span>
                            ) : (
                              card.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                                    color: "white",
                                    padding: "6px 16px",
                                    borderRadius: "20px",
                                    fontSize: "0.9rem",
                                    fontWeight: "600",
                                    boxShadow:
                                      "0 3px 10px rgba(139, 92, 246, 0.3)",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "translateY(-2px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 5px 15px rgba(139, 92, 246, 0.4)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                      "0 3px 10px rgba(139, 92, 246, 0.3)";
                                  }}
                                >
                                  {tag.name}
                                </span>
                              ))
                            )}
                          </div> */}
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
              borderRadius: "16px",
              border: "2px solid #fecaca",
            }}
          >
            <i
              className="bi bi-shield-lock"
              style={{
                fontSize: "4rem",
                color: "#fca5a5",
                marginBottom: "20px",
                display: "block",
              }}
            ></i>
            <p
              style={{
                fontSize: "1.3rem",
                color: "#dc2626",
                fontWeight: "600",
                margin: 0,
              }}
            >
              You don't have permission to view MCQ Questions.
            </p>
          </div>
        )}

        {/* Edit Modal */}
        <Dialog
          open={editModalOpen}
          onClose={handleCloseModal}
          PaperProps={{
            style: {
              borderRadius: "20px",
              padding: "10px 0",
              minWidth: "600px",
              maxWidth: "90vw",
                 "&::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
      borderRadius: "6px",
      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
    },
    // Custom scrollbar for Firefox
    scrollbarWidth: "thin",
    scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
  }}
            }
         
        >
          <DialogTitle
            style={{
              fontSize: "1.8rem",
              fontWeight: "800",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              paddingBottom: "10px",
              borderBottom: "2px solid #e5e7eb",
            }}
          >
            <i
              className="bi bi-pencil-square"
              style={{ marginRight: "10px" }}
            ></i>
            Edit Question
          </DialogTitle>
          <DialogContent style={{ paddingTop: "24px" }}>
            <DialogContentText
              style={{
                marginBottom: "24px",
                color: theme.palette.text.secondary,
                fontSize: "1.05rem",
              }}
            >
              Update your Question details below.
            </DialogContentText>
            <div style={{ marginBottom: "24px", position: "relative" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  fontWeight: "700",
                  fontSize: "1.1rem",
                  color: theme.palette.text.primary,
                }}
              >
                Question <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid #e0e7ff",
                  background: theme.palette.background.paper,
                    color: theme.palette.text.primary, 
                }}
              >
                <ReactQuill
                  value={editQuestion}
                  onChange={setEditQuestion}
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike"],
                      ["blockquote", "code-block"],
                      [{ header: 1 }, { header: 2 }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ script: "sub" }, { script: "super" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      [{ direction: "rtl" }],
                      [{ size: ["small", false, "large", "huge"] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ color: [] }, { background: [] }],
                      [{ font: [] }],
                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "font",
                    "size",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "blockquote",
                    "code-block",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                    "image",
                    "video",
                    "color",
                    "background",
                    "align",
                    "script",
                    { name: "preserve-spaces", type: "inline" },
                  ]}
                />
              </div>
            </div>

            <div>
              {editOptions.map((option, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "2px solid #e0e7ff",
                    borderRadius: "12px",
                    background:theme.palette.background.default,
                  }}
                >
                  <div style={{ marginBottom: "5px", fontWeight: "bold" }}>
                    Option {index + 1}
                  </div>
                  <ReactQuill
  value={option}
  onChange={(value) => handleEditOptionChange(index, value)}
  placeholder={`Enter option ${index + 1}`}
  modules={{
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      ["clean"],
    ],
  }}
  style={{
    marginBottom: "10px",
    borderRadius: "8px",
     background: theme.palette.background.paper,
                    color: theme.palette.text.primary, 
    border: "1px solid #e0e7ff",
  }}
/>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <label
                      style={{
                        marginRight: "30px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={editIsCorrect[index]}
                        onChange={(e) =>
                          handleEditCorrectChange(index, e.target.checked)
                        }
                        style={{ marginRight: "8px" }}
                      />
                      Correct Answer
                    </label>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleEditRemoveOption(index)}
                      style={{ color: "#ef4444" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                  <div style={{ marginBottom: "5px", fontWeight: "bold" }}>
                    Explanation
                  </div>
                 <ReactQuill
  value={editOptionExplanations[index]}
  onChange={(value) => handleEditExplanationChange(index, value)}
  placeholder={`Explanation for option ${index + 1} (optional)`}
  modules={{
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      ["clean"],
    ],
  }}
  style={{
    marginBottom: "10px",
    borderRadius: "8px",
     background: theme.palette.background.paper,
                    color: theme.palette.text.primary, 
    border: "1px solid #e0e7ff",
  }}
/>
                </div>
              ))}
              <Button
                onClick={addEditOption}
                variant="contained"
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  padding: "10px 20px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                  },
                }}
              >
                Add Option
              </Button>
            </div>

            <div style={{ marginTop: "20px" }}>
              <label
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "8px",
                  color: theme.palette.text.secondary,
                }}
              >
                Upload Image (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEditSelectedImage(e.target.files[0])}
                style={{
                  display: "block",
                  border: "1px solid #ccc",
                  padding: "12px",
                  borderRadius: "8px",
                  width: "100%",
                  background: theme.palette.background.default,
                }}
              />
              {editSelectedImage && (
                <p
                  style={{
                    marginTop: "5px",
                    fontSize: "14px",
                    color: "#10b981",
                    fontWeight: "600",
                  }}
                >
                  Selected: {editSelectedImage.name}
                </p>
              )}
            </div>
          </DialogContent>
          <DialogActions style={{ padding: "20px 24px" }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                color: theme.palette.text.primary,
                padding: "10px 24px",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: "10px",
                textTransform: "none",
                 "&:hover": {
                 background: (theme) =>
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : '#f3f4f6',
              },
            }}
             
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCard}
              disabled={!(permissions.can_update_mcq || permissions.is_admin)}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "10px 28px",
                fontSize: "1rem",
                fontWeight: "700",
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "#d1d5db",
                  color: "white",
                  opacity: 0.6,
                },
              }}
            >
              <i
                className="bi bi-check-circle"
                style={{ marginRight: "8px" }}
              ></i>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "12px",
              padding: "10px",
            },
          }}
        >
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              fontWeight: "bold",
              color: "error.main",
              fontSize: "1.3rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <DeleteIcon sx={{ color: "error.main", mr: 1 }} />
            Delete Question
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                Are you sure you want to delete this Question? <br />
                This action cannot be undone.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
            <Button
              onClick={handleCloseDeleteDialog}
              variant="outlined"
              color="inherit"
              sx={{
                borderColor: "grey.400",
                padding: "8px 20px",
                fontSize: "0.9rem",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCard}
              disabled={!(permissions.can_delete_mcq || permissions.is_admin)}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{
                boxShadow: "none",
                padding: "8px 20px",
                fontSize: "0.9rem",
                textTransform: "none",
              }}
            >
              Delete Permanently
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Dialog */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={handleCancelDuplicate}
          PaperProps={{
            style: {
              borderRadius: "20px",
              padding: "10px",
              minWidth: "500px",
            },
          }}
        >
          <DialogTitle
            style={{
              fontSize: "1.8rem",
              fontWeight: "800",
              color: "#f59e0b",
              paddingBottom: "10px",
              borderBottom: "2px solid #fef3c7",
            }}
          >
            <i
              className="bi bi-exclamation-circle"
              style={{ marginRight: "10px" }}
            ></i>
            Duplicate Question
          </DialogTitle>
          <DialogContent style={{ paddingTop: "24px" }}>
            <DialogContentText
              style={{
                color: "#374151",
                fontSize: "1.1rem",
                lineHeight: "1.6",
              }}
            >
              A similar question already exists. Do you want to add it anyway?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: "20px 24px" }}>
            <Button
              onClick={handleCancelDuplicate}
              sx={{
                color: "#6b7280",
                padding: "10px 24px",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": {
                  background: "#f3f4f6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDuplicate}
              disabled={!(permissions.can_create_mcq || permissions.is_admin)}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "white",
                padding: "10px 28px",
                fontSize: "1rem",
                fontWeight: "700",
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 15px rgba(245, 158, 11, 0.4)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
                  boxShadow: "0 6px 20px rgba(245, 158, 11, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "#d1d5db",
                  color: "white",
                  opacity: 0.6,
                },
              }}
            >
              <i
                className="bi bi-plus-circle"
                style={{ marginRight: "8px" }}
              ></i>
              Add Anyway
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              fontSize: "1rem",
              fontWeight: "600",
              borderRadius: "12px",
              boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Tag Modal */}
        <TagModal
          open={tagModalOpen}
          position={tagModalPosition}
          onClose={() => setTagModalOpen(false)}
          cardId={cards[tagCardIndex]?.id}
          cardQuestionType="MCQ"
          onTagAdded={handleTagAdded}
          onTagUpdated={handleTagUpdated}
          onTagDeleted={handleTagDeleted}
          onShowSnackbar={showSnackbar}
        />
      </div>

      <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
  <Box
    sx={{
      p: 0,
      bgcolor: "background.paper",
      mx: "auto",
      mt: "3%",
      mb: "3%",
      width: "90%",
      maxWidth: 800,
      maxHeight: "90vh",
      borderRadius: "16px",
      boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
         "&::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
      borderRadius: "6px",
      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
    },
    // Custom scrollbar for Firefox
    scrollbarWidth: "thin",
    scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
  }}
   
  >
    {/* Header */}
    <Box
      sx={{
        p: 3.5,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
          User Guide
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Multiple Choice Questions
        </Typography>
      </Box>
    </Box>

    {/* Content */}
    <Box sx={{ p: 4, overflowY: "auto", flex: 1 }}>
      {/* Introduction */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <i className="bi bi-info-circle" style={{ color: "white", fontSize: "20px" }}></i>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main}}>
            Introduction
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
          Welcome to the Multiple Choice Questions page! This tool allows you to create, edit, delete, and organize multiple-choice questions for quizzes. You can add questions, options, explanations, images, and tags to categorize your content.
        </Typography>
      </Box>

      {/* Creating a Question */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <i className="bi bi-plus-circle" style={{ color: "white", fontSize: "20px" }}></i>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "600", color:theme.palette.primary.main }}>
            Creating a New Question
          </Typography>
        </Box>
        <Box sx={{ pl: 7, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            1. Click the "Question" bar to expand it and enter your question in the rich text editor.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            2. Click the "Options" bar to expand it and add multiple options for your question.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            3. For each option, add an explanation (optional) and mark the correct answer(s).
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            4. Optionally, upload an image to accompany your question.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            5. Click "Add Question" to save.
          </Typography>
        </Box>
      </Box>

      {/* Managing Questions */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <i className="bi bi-gear" style={{ color: "white", fontSize: "20px" }}></i>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main }}>
            Managing Questions
          </Typography>
        </Box>
        <Box sx={{ pl: 7, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            - Click the <strong>expand icon (<i className="bi bi-arrows-angle-expand" style={{ color: "#667eea", fontSize: "16px" }}></i>)</strong> to view the options and explanations.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            - Click the <strong>edit icon (<i className="bi bi-pencil-square" style={{ color: "#10b981", fontSize: "16px" }}></i>)</strong> to update a question.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            - Click the <strong>delete icon (<i className="bi bi-trash3" style={{ color: "#ef4444", fontSize: "16px" }}></i>)</strong> to remove a question.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            - Click the <strong>tag icon (<i className="bi bi-tags" style={{ color: "#8b5cf6", fontSize: "16px" }}></i>)</strong> to add or manage tags.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            - Click the <strong>Show Image</strong> button to view the uploaded image.
          </Typography>
        </Box>
      </Box>

      {/* Rich Text Editor */}
      <Box sx={{ mb: 4 }}>
  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 2,
      }}
    >
      <i className="bi bi-pencil-square" style={{ color: "white", fontSize: "20px" }}></i>
    </Box>
    <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main }}>
      Using the Rich Text Editor
    </Typography>
  </Box>
  <Box sx={{ pl: 7, display: "flex", flexDirection: "column", gap: 1.5 }}>
    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
      The rich text editor allows you to format your questions, answers, and explanations with a variety of tools. Here's how to use them:
    </Typography>

    {/* Basic Formatting */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Basic Formatting:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Bold:</strong> Select text and click <strong>B</strong> or press <strong>Ctrl+B</strong>.
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <em>Italic:</em> Select text and click <em>I</em> or press <strong>Ctrl+I</strong>.
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <u>Underline:</u> Select text and click <u>U</u> or press <strong>Ctrl+U</strong>.
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <s>Strikethrough:</s> Select text and click <s>S</s>.
      </Typography>
    </Box>

    {/* Lists */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Lists:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Bullet List:</strong> Click the bullet list icon (<i className="bi bi-list-ul" style={{ fontSize: "14px" }}></i>).
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Numbered List:</strong> Click the numbered list icon (<i className="bi bi-list-ol" style={{ fontSize: "14px" }}></i>).
      </Typography>
    </Box>

    {/* Subscript and Superscript */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Subscript and Superscript:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Subscript:</strong> Select text and click <strong>X<sub>2</sub></strong> or use the shortcut <strong>Ctrl+,</strong>.
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Superscript:</strong> Select text and click <strong>X<sup>2</sup></strong> or use the shortcut <strong>Ctrl+.</strong>.
      </Typography>
    </Box>

    {/* LaTeX for Equations */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      LaTeX for Equations:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Inline LaTeX:</strong> Wrap your equation in <code>$...$</code>. Example: <code>$E = mc^2$</code> renders as {` `}
        <span dangerouslySetInnerHTML={{ __html: katex.renderToString("E = mc^2", { displayMode: false }) }}></span>.
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Block LaTeX:</strong> Wrap your equation in <code>$$...$$</code>. Example: {` `}
        <code>
          $$
          \int_a^b f(x) \,dx
          $$
        </code> renders as {` `}
        <div dangerouslySetInnerHTML={{ __html: katex.renderToString("\\int_a^b f(x) \\,dx", { displayMode: true }) }}></div>.
      </Typography>
    </Box>

    {/* Headers */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Headers:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • Select text and choose a header size from the dropdown (e.g., <strong>H1, H2</strong>).
      </Typography>
    </Box>

    {/* Indentation */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Indentation:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Increase Indent:</strong> Click the <strong>Increase Indent</strong> icon (<i className="bi bi-text-indent-right" style={{ fontSize: "14px" }}></i>).
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • <strong>Decrease Indent:</strong> Click the <strong>Decrease Indent</strong> icon (<i className="bi bi-text-indent-left" style={{ fontSize: "14px" }}></i>).
      </Typography>
    </Box>

    {/* Text Alignment */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Text Alignment:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • Align text to the <strong>left</strong>, <strong>center</strong>, or <strong>right</strong> using the alignment icons.
      </Typography>
    </Box>

    {/* Code Blocks */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Code Blocks:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • Click the <strong>Code Block</strong> icon (<i className="bi bi-code" style={{ fontSize: "14px" }}></i>) to insert a code block.
      </Typography>
    </Box>

    {/* Colors and Background */}
    <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
      Colors and Background:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
        • Change text color or background color using the color picker icons.
      </Typography>
    </Box>

    {/* Links and Images */}
    {/* <Typography variant="subtitle2" sx={{ fontWeight: "600", color: "#2d3748", mt: 1 }}>
      Links and Images:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.7 }}>
        • <strong>Insert Link:</strong> Select text and click the link icon (<i className="bi bi-link" style={{ fontSize: "14px" }}></i>).
      </Typography>
      <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.7 }}>
        • <strong>Insert Image:</strong> Click the image icon (<i className="bi bi-image" style={{ fontSize: "14px" }}></i>).
      </Typography>
    </Box> */}

    {/* Clean Formatting */}
    {/* <Typography variant="subtitle2" sx={{ fontWeight: "600", color: "#2d3748", mt: 1 }}>
      Clean Formatting:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.7 }}>
        • Click the <strong>Clean Formatting</strong> icon (<i className="bi bi-eraser" style={{ fontSize: "14px" }}></i>) to remove all formatting.
      </Typography>
    </Box> */}
  </Box>
</Box>

      {/* Tips */}
      <Box
        sx={{
          p: 3,
          borderRadius: "12px",
          bgcolor: theme.palette.background.default,
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.primary.main, mb: 2 }}>
          💡 Quick Tips
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            • Use LaTeX for mathematical or scientific content.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            • Add explanations to help users understand the answer.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            • Use tags to categorize questions (e.g., "Math", "Physics").
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            • Upload images to make questions more engaging.
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* Footer */}
    <Box
      sx={{
        p: 3,
        bgcolor: theme.palette.background.paper,
        borderTop: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <Button
        variant="contained"
        onClick={() => setInfoModalOpen(false)}
        sx={{
          minWidth: 120,
          borderRadius: "10px",
          padding: "10px 28px",
          fontWeight: "600",
          textTransform: "none",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
          "&:hover": {
            background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
            boxShadow: "0 6px 16px rgba(102, 126, 234, 0.4)",
          },
        }}
      >
        Got it
      </Button>
    </Box>
  </Box>
</Modal>

    </div>
  );
};

export default MultipleChoice;
